import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { PaidPlanTier } from '@tabbio/types';
import { useLocale } from '../../hooks/useLocale';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { useCheckoutFlow } from '../../hooks/useCheckoutFlow';
import { usePersistOnboardingPreferences } from '../../hooks/usePersistOnboardingPreferences';
import {
  captureProductException,
  trackProductEvent,
} from '../../lib/product-analytics';
import { useOnboarding } from './OnboardingProvider';
import {
  clearPendingOnboardingAiPlanCheckout,
  markOnboardingAiPlanCheckoutConfirmed,
  pendingOnboardingAiPlanCheckoutMatchesUser,
  readPendingOnboardingAiPlanCheckout,
  wasOnboardingAiPlanCheckoutConfirmed,
  writePendingOnboardingAiPlanCheckout,
} from '../../lib/onboarding-plan-checkout';
import {
  getIndividualPaidSubscriptionTier,
  subscriptionStateMeetsPurchaseTier,
} from '../../lib/subscription-purchase';
import {
  CHOOSER_BENEFIT_KEYS,
  CHOOSER_TIERS,
  PLAN_PRESENTATION,
} from '../../lib/subscription-journey/plan-presentation';
import { getTierPrices } from '../../lib/subscription-journey/pricing';
import type { CheckoutSelection } from '../../lib/subscription-journey/checkout-flow';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WritingLevel } from '../subscription/WritingLevel.web';
import { ModelFamilyRow } from '../subscription/ModelFamilyRow.web';
import { TABBIO_CV_MARK, toAssetSrc } from '../subscription/nova-assets';
import {
  CheckoutReviewActions,
  CheckoutReviewBody,
} from '../subscription/CheckoutReview.web';
import { ComparePlansDialog } from '../subscription/ComparePlansDialog.web';
import {
  Icon,
  LegalLinks,
  SubButton,
  SubInfo,
  SubList,
  TierMark,
} from '../subscription/journey-ui.web';

type PlanKey = 'pro' | 'plus' | 'free';

/** A failed plan save, shown inline with Retry (parity with the native sheet). */
type SaveFailure = { plan: PlanKey | 'skip' };

const TIER_NAMES: Record<PaidPlanTier, string> = { plus: 'Plus', pro: 'Pro' };
const CV_MARK_SRC = toAssetSrc(TABBIO_CV_MARK);

/** The CV silhouette beside the title, tinted with the journey violet. */
function CvMark({ size = 44 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'block',
        flexShrink: 0,
        width: Math.round(size * (413 / 510)),
        height: size,
        backgroundColor: 'var(--sub-brand)',
        WebkitMaskImage: `url(${CV_MARK_SRC})`,
        maskImage: `url(${CV_MARK_SRC})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
    />
  );
}

/**
 * A: "How should we write your CV?" — the soft plan picker (the ONE
 * monetization step in onboarding). Free is always one visible tap: the Free
 * row + "Create my free CV", the standing continue-free link under a paid
 * pick, and the close button. A paid pick opens the in-place review step (C);
 * only a synced snapshot that meets the tier advances with that tier.
 */
export function StepChooseAI() {
  const { t } = useTranslation(['auth', 'common']);
  const { isRTL } = useLocale();
  const { nextStep, startCvEnrichment } = useOnboarding();
  const params = useLocalSearchParams<{ checkout?: string }>();
  const { user } = useAuth();
  const { syncSubscription } = useRevenueCat();
  const { catalog } = useSubscriptionState();
  const toast = useToast();
  const [selected, setSelected] = useState<PlanKey>('plus');
  const [compareOpen, setCompareOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [saveFailure, setSaveFailure] = useState<SaveFailure | null>(null);
  const checkoutReturnHandledRef = useRef(false);
  const { isPersistingPreferences, persistOnboardingPreferences } =
    usePersistOnboardingPreferences({ step: 'choose-ai' });

  const persistPlanAndAdvance = useCallback(
    async (plan: PlanKey | 'skip') => {
      setSaveFailure(null);
      const didPersist = await persistOnboardingPreferences({
        selectedAiPlan: plan,
      });
      if (!didPersist) {
        // Inline, with Retry: the primary action is never gated on a toast.
        setSaveFailure({ plan });
        return false;
      }

      // The import handler already kicked the free-tier enrichment run the
      // moment the CV was persisted; only a PAID pick re-runs it so the
      // premium model rewrites the draft (once per uploaded CV). Free/skip
      // must not restart work that is already underway.
      const paid = plan === 'plus' || plan === 'pro';
      if (paid) {
        startCvEnrichment({ paidUpgrade: true });
      }
      // A paid pass-through replaces this entry, so browser Back never lands
      // on a chooser that would only pass the user on again.
      await nextStep(
        paid ? { fromStep: 'choose-ai', historyMode: 'replace' } : { fromStep: 'choose-ai' },
      );
      return true;
    },
    [nextStep, persistOnboardingPreferences, startCvEnrichment],
  );

  // Only a synced snapshot that meets the tier lands here (never a tap). The
  // CV build continues: onboarding never shows the Welcome screen.
  const onPurchased = useCallback(
    (selection: CheckoutSelection) => {
      clearPendingOnboardingAiPlanCheckout();
      trackProductEvent('onboarding_ai_plan_purchase_completed', {
        plan: selection.tier,
        surface: 'web',
      });
      void persistPlanAndAdvance(selection.tier);
    },
    [persistPlanAndAdvance],
  );
  const checkout = useCheckoutFlow({ planSet: 'individual', onPurchased });
  const status = checkout.state.status;
  const inReview = status !== 'picker' && status !== 'purchased';

  const isProcessing =
    isSubmitting ||
    isCheckingSubscription ||
    isPersistingPreferences ||
    checkout.isBusy;

  // A purchase that did not complete leaves no paid choice behind: the
  // pending web checkout marker only survives a redirect to hosted checkout.
  useEffect(() => {
    if (status === 'cancelled' || status === 'error') {
      clearPendingOnboardingAiPlanCheckout();
    }
    if (status === 'error') {
      trackProductEvent('onboarding_ai_plan_purchase_failed', {
        plan: checkout.state.selection?.tier ?? 'unknown',
        reason: checkout.state.failure ?? 'unknown',
      });
      captureProductException(new Error('Onboarding checkout failed'), {
        feature: 'onboarding',
        action: 'choose_ai_checkout',
        plan: checkout.state.selection?.tier ?? 'unknown',
        reason: checkout.state.failure ?? 'unknown',
      });
    }
    if (status === 'redirecting') {
      trackProductEvent('onboarding_ai_plan_checkout_redirected', {
        plan: checkout.state.selection?.tier ?? 'unknown',
      });
    }
  }, [checkout.state.failure, checkout.state.selection?.tier, status]);

  useEffect(() => {
    if (checkoutReturnHandledRef.current) return;
    checkoutReturnHandledRef.current = true;
    setIsCheckingSubscription(true);

    void (async () => {
      const pendingCheckout =
        params.checkout === 'success'
          ? readPendingOnboardingAiPlanCheckout()
          : null;
      // Back onto the `?checkout=success` entry after its checkout was
      // confirmed is a plain visit, not a checkout we lost.
      const isCheckoutReturn =
        params.checkout === 'success' &&
        (pendingCheckout !== null || !wasOnboardingAiPlanCheckoutConfirmed());

      if (isCheckoutReturn) {
        if (!pendingCheckout) {
          toast.error(
            t('onboarding.chooseAI.checkoutFailedTitle', {
              defaultValue: 'Checkout failed',
            }),
            t('onboarding.chooseAI.checkoutMissing', {
              defaultValue:
                'We could not confirm this checkout. Please choose a plan again.',
            }),
          );
          return;
        }

        if (!pendingOnboardingAiPlanCheckoutMatchesUser(pendingCheckout, user?.id)) {
          clearPendingOnboardingAiPlanCheckout();
          toast.error(
            t('onboarding.chooseAI.checkoutFailedTitle', {
              defaultValue: 'Checkout failed',
            }),
            t('onboarding.chooseAI.checkoutWrongUser', {
              defaultValue: 'This checkout belongs to a different account.',
            }),
          );
          return;
        }

        setSelected(pendingCheckout.plan);
        const result = await syncSubscription({
          planSet: 'individual',
          companyId: null,
        });
        const requestedPurchase = {
          planSet: 'individual' as const,
          tier: pendingCheckout.plan,
          companyId: null,
        };
        if (!subscriptionStateMeetsPurchaseTier(result, requestedPurchase)) {
          trackProductEvent('onboarding_ai_plan_checkout_not_confirmed', {
            plan: pendingCheckout.plan,
          });
          toast.error(
            t('onboarding.chooseAI.checkoutFailedTitle', {
              defaultValue: 'Checkout not confirmed',
            }),
            t('onboarding.chooseAI.checkoutNotConfirmed', {
              defaultValue:
                'Your subscription is still being confirmed. Please try again.',
            }),
          );
          return;
        }

        clearPendingOnboardingAiPlanCheckout();
        markOnboardingAiPlanCheckoutConfirmed();
        await persistPlanAndAdvance(pendingCheckout.plan);
        return;
      }

      const result = await syncSubscription({
        planSet: 'individual',
        companyId: null,
      });
      const paidTier = getIndividualPaidSubscriptionTier(result);
      if (!paidTier) return;

      setSelected(paidTier);
      trackProductEvent('onboarding_ai_plan_existing_paid', {
        plan: paidTier,
        placement: 'onboarding_ai_choice',
      });
      await persistPlanAndAdvance(paidTier);
    })()
      .catch(() => {
        // A failed regular entitlement lookup leaves the Free path available.
        if (params.checkout === 'success') {
          toast.error(
            t('onboarding.chooseAI.checkoutFailedTitle'),
            t('onboarding.chooseAI.checkoutFailed'),
          );
        }
      })
      .finally(() => setIsCheckingSubscription(false));
  }, [params.checkout, persistPlanAndAdvance, syncSubscription, t, toast, user?.id]);

  const handleSelect = async () => {
    if (isProcessing) return;
    trackProductEvent('onboarding_ai_plan', {
      plan: selected,
      placement: 'onboarding_ai_choice',
    });

    if (selected === 'free') {
      setIsSubmitting(true);
      clearPendingOnboardingAiPlanCheckout();
      await persistPlanAndAdvance(selected);
      setIsSubmitting(false);
      return;
    }

    // "Continue with Plus/Pro" opens the review step; nothing is charged or
    // persisted until the provider confirms.
    checkout.openReview({ tier: selected, cycle: 'monthly' });
  };

  // "Continue to Web billing": the web checkout redirects away, so the tier is
  // parked for the `?checkout=success` return handled above.
  const handlePurchase = () => {
    const tier = checkout.state.selection?.tier;
    if (!tier || checkout.isBusy) return;
    writePendingOnboardingAiPlanCheckout(tier, { userId: user?.id });
    void checkout.purchase();
  };

  const handleClose = async () => {
    if (isProcessing) return;

    setIsSubmitting(true);
    trackProductEvent('onboarding_ai_plan', { plan: 'skip' });
    clearPendingOnboardingAiPlanCheckout();
    await persistPlanAndAdvance('skip');
    setIsSubmitting(false);
  };

  // The soft-paywall contract: continuing free is always one visible tap,
  // never hidden behind selecting the Free card first.
  const handleContinueFree = async () => {
    if (isProcessing) return;

    setIsSubmitting(true);
    trackProductEvent('onboarding_ai_plan', {
      plan: 'free',
      placement: 'onboarding_ai_choice',
      via: 'continue_free_link',
    });
    clearPendingOnboardingAiPlanCheckout();
    await persistPlanAndAdvance('free');
    setIsSubmitting(false);
  };

  // "Restore": an existing paid subscription advances like the auto-advance.
  const handleRestore = async () => {
    if (isProcessing) return;
    setIsSubmitting(true);
    try {
      const result = await syncSubscription({ planSet: 'individual', companyId: null });
      const paidTier = getIndividualPaidSubscriptionTier(result);
      if (paidTier) {
        setSelected(paidTier);
        await persistPlanAndAdvance(paidTier);
        return;
      }
      toast.show({ title: t('common:subscription.checkout.unchanged'), type: 'info' });
    } catch {
      toast.error(
        t('common:subscription.states.error.title'),
        t('common:subscription.states.error.body'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Retry re-runs exactly the choice whose save failed; a verified purchase
  // is never repeated.
  const handleRetrySave = async () => {
    if (isProcessing || !saveFailure) return;
    setIsSubmitting(true);
    await persistPlanAndAdvance(saveFailure.plan);
    setIsSubmitting(false);
  };

  const saveFailureNotice = saveFailure ? (
    <div role="alert" data-testid="chooserNotice" style={{ display: 'grid', gap: 8 }}>
      <SubInfo icon="warning" warning>
        {t('onboarding.chooseAI.saveFailedTitle')} {t('onboarding.chooseAI.saveFailedBody')}
      </SubInfo>
      <SubButton
        kind="text"
        onClick={handleRetrySave}
        disabled={isProcessing}
        aria-busy={isSubmitting || isPersistingPreferences}
        icon={<Icon name="refresh" />}
      >
        {t('common:actions.retry')}
      </SubButton>
    </div>
  ) : null;

  const selectedPrices =
    selected === 'free' ? null : getTierPrices(catalog, 'individual', selected);
  const continueFreeLink = t('onboarding.chooseAI.continueWithFree');
  const ctaLabel =
    selected === 'free'
      ? t('onboarding.chooseAI.createFreeCv')
      : t('onboarding.chooseAI.continueWithTier', { tier: TIER_NAMES[selected] });

  const closeButton = (
    <button
      type="button"
      className="tabbio-sub-icon-btn"
      onClick={handleClose}
      disabled={isProcessing}
      aria-label={t('onboarding.chooseAI.close')}
    >
      <MaterialSymbol name="close" size={22} />
    </button>
  );

  const reviewSelection = checkout.state.selection;
  const reviewPrices = reviewSelection
    ? getTierPrices(catalog, 'individual', reviewSelection.tier)
    : null;

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="tabbio-sub"
      style={{ flex: 1, minHeight: 0 }}
    >
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 0 20px' }}>
        <div className="tabbio-sub-column">
          {inReview ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '44px 1fr 44px',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <button
                  type="button"
                  className="tabbio-sub-icon-btn"
                  style={{ background: 'transparent' }}
                  onClick={() => checkout.backToPicker()}
                  disabled={
                    checkout.isBusy ||
                    status === 'pending' ||
                    status === 'checking'
                  }
                  aria-label={t('common:subscription.actions.back')}
                >
                  <Icon name="chevron_left" size={26} flip />
                </button>
                <h2
                  style={{
                    margin: 0,
                    textAlign: 'center',
                    fontSize: 17,
                    fontWeight: 500,
                  }}
                >
                  {t('common:subscription.checkout.title')}
                </h2>
                {closeButton}
              </div>
              <div className="tabbio-sub-step-enter" key="review">
                <CheckoutReviewBody
                  state={checkout.state}
                  prices={reviewPrices}
                  onChangePlan={() => checkout.backToPicker()}
                />
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  margin: '8px 0 24px',
                }}
              >
                <CvMark size={46} />
                <h1 className="tabbio-sub-heading" style={{ flex: 1 }}>
                  {t('onboarding.chooseAI.title')}
                </h1>
                {closeButton}
              </div>

              <div
                role="radiogroup"
                aria-label={t('onboarding.chooseAI.groupLabel')}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {CHOOSER_TIERS.map((tier) => {
                  const isSel = tier === selected;
                  const presentation = PLAN_PRESENTATION[tier];
                  const prices =
                    tier === 'free' ? null : getTierPrices(catalog, 'individual', tier);
                  const featuresId = `choose-ai-features-${tier}`;
                  return (
                    <div
                      key={tier}
                      className={`tabbio-sub-plan${isSel ? ' is-selected' : ''}`}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isSel}
                        aria-controls={tier === 'free' ? undefined : featuresId}
                        aria-expanded={tier === 'free' ? undefined : isSel}
                        className="tabbio-sub-plan-btn"
                        disabled={isProcessing}
                        onClick={() => setSelected(tier)}
                      >
                        <span className={`tabbio-sub-circle${isSel ? ' is-on' : ''}`} aria-hidden>
                          {isSel ? <MaterialSymbol name="check" /> : null}
                        </span>
                        <span className="tabbio-sub-plan-copy">
                          <span className="tabbio-sub-plan-name">
                            {tier === 'free'
                              ? t('onboarding.chooseAI.freeLabel')
                              : TIER_NAMES[tier]}
                            <TierMark tier={tier} />
                          </span>
                          <WritingLevel
                            level={presentation.writingLevel}
                            label={t(presentation.writingLabelKey)}
                          />
                          {presentation.model ? (
                            <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px 8px' }}>
                              <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
                                {t('onboarding.chooseAI.aiModel')}
                              </span>
                              <ModelFamilyRow family={presentation.model} fontSize={14} size={18} />
                            </span>
                          ) : (
                            <span className="tabbio-sub-plan-desc">
                              {t('onboarding.chooseAI.freeDescription')}
                            </span>
                          )}
                        </span>
                        <span className="tabbio-sub-plan-price">
                          {tier === 'free' ? (
                            t('onboarding.chooseAI.freeLabel')
                          ) : (
                            <>
                              <span dir="ltr">{prices?.monthlyDisplay ?? ''}</span>
                              <small>{t('onboarding.chooseAI.perMonth')}</small>
                            </>
                          )}
                        </span>
                      </button>
                      {tier !== 'free' && isSel ? (
                        <div id={featuresId} className="tabbio-sub-plan-features">
                          <SubList
                            items={CHOOSER_BENEFIT_KEYS[tier].map((key) => t(key))}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <button
                  type="button"
                  className="tabbio-sub-link"
                  onClick={() => setCompareOpen(true)}
                >
                  {t('onboarding.chooseAI.seeAllFeatures')}
                  <Icon name="chevron_right" size={20} flip />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--sub-line)', padding: '14px 0 24px' }}>
        <div className="tabbio-sub-column tabbio-sub-actions">
          {inReview ? (
            <>
              {saveFailureNotice}
              <CheckoutReviewActions
                state={checkout.state}
                onPurchase={handlePurchase}
                onRestore={() => void checkout.restore()}
                onCheckAgain={() => void checkout.checkAgain()}
                onBackToTabbio={handleClose}
              />
            </>
          ) : (
            <>
              {/* `.subscription-offer` footer: pill, one short disclosure, then
                  the quiet Free alternative. Restore / Terms / Privacy stay here
                  by product decision (Apple's sign-up screen requirements). */}
              {saveFailureNotice}
              <SubButton className="is-offer" onClick={handleSelect} disabled={isProcessing} aria-busy={isProcessing}>
                {ctaLabel}
              </SubButton>
              <p className="tabbio-sub-disclosure is-offer">
                {selected === 'free'
                  ? t('onboarding.chooseAI.disclosureFree')
                  : t('onboarding.chooseAI.disclosurePaid', {
                      price: selectedPrices?.monthlyDisplay ?? '',
                    })}
              </p>
              {selected !== 'free' ? (
                <SubButton kind="text" onClick={handleContinueFree} disabled={isProcessing}>
                  {continueFreeLink}
                </SubButton>
              ) : null}
              <LegalLinks onRestore={handleRestore} restoreDisabled={isProcessing} />
            </>
          )}
        </div>
      </div>

      {compareOpen ? (
        <ComparePlansDialog onClose={() => setCompareOpen(false)} />
      ) : null}
    </div>
  );
}
